import React, { useState } from 'react';
import {
  Button,
  Flex,
  Popconfirm,
  Table,
  TableColumnProps,
  Tag,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ActionStatus,
  Issue,
  JourneyCollection,
  JourneyState,
} from 'interfaces/journey';
import {
  useReleaseCollectionWait,
  useRemoveCollection,
} from 'services/journeys';
import { formatAmount, hasAmount } from 'util/amount';
import { collectionAmountsEnabled } from 'constants/flags';
import CollectionDetailModal from 'routes/app/collections/CollectionDetailModal';
import SITELINKS from 'constants/sitelinks';
import { collectionWindow, declaredText } from 'services/CollectionsV2Service';
import CollectionIntakeModal from './CollectionIntakeModal';
import { issueColor } from './issueColor';
import {
  isIngested,
  isTransferred,
  statusLabel,
} from './collectionStatusLabel';
import { useJourneyDashboardContext } from '../context';

export interface TableCollection {
  collectionId: number;
  collection?: JourneyCollection;
  actionStatus?: ActionStatus;
  deliveryStatus?: ActionStatus;
  arrivedAt?: string;
  issue?: Issue;
  collectionStatus?: string;
  transferredTo?: string;
}

interface Props {
  collections: TableCollection[];
}

const ACTION_STATUS_DESCRIPTOR: Record<
  ActionStatus,
  { label: string; icon?: React.ReactNode }
> = {
  [ActionStatus.Pending]: { label: 'Pendiente' },
  [ActionStatus.Completed]: {
    label: 'Ingresada',
    icon: <CheckOutlined style={{ color: 'green', fontSize: 18 }} />,
  },
  [ActionStatus.Failed]: {
    label: 'No colectada',
    icon: <CloseOutlined style={{ color: 'red', fontSize: 18 }} />,
  },
  [ActionStatus.Forced_end]: {
    label: 'No colectada',
    icon: <CloseOutlined style={{ color: 'red', fontSize: 18 }} />,
  },
  [ActionStatus.Canceled]: {
    label: 'Cancelada',
    icon: <CloseOutlined style={{ color: 'red', fontSize: 18 }} />,
  },
  [ActionStatus.Transferred]: {
    label: 'Transferida',
    icon: <SwapOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />,
  },
};

const issueStyle = (row: TableCollection): React.CSSProperties | undefined =>
  row.issue ? { color: issueColor(row.issue.blame) } : undefined;

const CollectionsTable: React.FC<Props> = ({ collections }: Props) => {
  const { state, id, refetch } = useJourneyDashboardContext();
  const [intakeId, setIntakeId] = useState<number | null>(null);
  const { mutate: removeCollection, isPending } = useRemoveCollection();
  const [toRemove, setToRemove] = useState<number | null>(null);
  const { mutate: releaseWait, isPending: isReleasing } =
    useReleaseCollectionWait();
  const [detailId, setDetailId] = useState<number | null>(null);

  if (!collections || collections.length === 0) return null;

  const hasOnDemand = collections.some(
    (row) => row.collection?.type === 'on_demand',
  );

  const storeColumn: TableColumnProps<TableCollection> = {
    title: 'Colecta',
    width: 200,
    ellipsis: true,
    render: (row: TableCollection) => {
      const style =
        row.actionStatus === ActionStatus.Canceled
          ? { textDecoration: 'line-through', color: 'darkgray' }
          : issueStyle(row);
      const name = row.collection?.storeName ?? `Colecta ${row.collectionId}`;
      if (row.collection?.type !== 'on_demand') {
        return <span style={style}>{name}</span>;
      }
      const declared = declaredText(
        row.collection.declaredQuantity,
        row.collection.declaredDescription,
      );
      const { notes } = row.collection;
      return (
        <Tooltip
          title={
            <>
              {declared ? `A demanda · ${declared}` : 'A demanda'}
              {notes && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  Observaciones: {notes}
                </div>
              )}
            </>
          }
        >
          <span style={style}>
            {name} <Tag color="magenta">A demanda</Tag>
          </span>
        </Tooltip>
      );
    },
    sorter: (a, b) =>
      (a.collection?.storeName ?? '').localeCompare(
        b.collection?.storeName ?? '',
      ),
  };

  const startColumn: TableColumnProps<TableCollection> = {
    title: 'Hora',
    width: hasOnDemand ? 120 : 70,
    render: (row: TableCollection) => {
      const start = row.collection?.scheduledStart;
      if (!start) return <span style={{ color: '#8c8c8c' }}>-</span>;
      return (
        <span style={issueStyle(row)}>
          {row.collection?.type === 'on_demand'
            ? collectionWindow(start, row.collection.scheduledEnd)
            : dayjs(start).format('HH:mm')}
        </span>
      );
    },
    sorter: (a, b) =>
      (a.collection?.scheduledStart ?? '').localeCompare(
        b.collection?.scheduledStart ?? '',
      ),
  };

  const durationColumn: TableColumnProps<TableCollection> = {
    title: <span style={{ whiteSpace: 'nowrap' }}>Duración</span>,
    width: 95,
    align: 'center',
    render: (row: TableCollection) => {
      const minutes = row.collection?.estimatedDurationMinutes;
      if (!minutes) return <span style={{ color: '#8c8c8c' }}>-</span>;
      return <span style={issueStyle(row)}>{minutes} min</span>;
    },
    sorter: (a, b) =>
      (a.collection?.estimatedDurationMinutes ?? 0) -
      (b.collection?.estimatedDurationMinutes ?? 0),
  };

  const amountColumn: TableColumnProps<TableCollection> = {
    title: <span style={{ whiteSpace: 'nowrap' }}>Valor</span>,
    width: 130,
    align: 'right',
    render: (row: TableCollection) => {
      const amount = row.collection?.amount;
      return (
        <span
          style={
            hasAmount(amount)
              ? { whiteSpace: 'nowrap', ...issueStyle(row) }
              : { whiteSpace: 'nowrap', color: '#8c8c8c' }
          }
        >
          {formatAmount(amount)}
        </span>
      );
    },
    sorter: (a, b) =>
      (a.collection?.amount ?? -1) - (b.collection?.amount ?? -1),
  };

  const statusColumn: TableColumnProps<TableCollection> = {
    title: 'Estado',
    width: 110,
    ellipsis: true,
    render: (row: TableCollection) => (
      <span style={issueStyle(row)}>{statusLabel(row)}</span>
    ),
    sorter: (a, b) => statusLabel(a).localeCompare(statusLabel(b)),
  };

  const actionStatusColumn: TableColumnProps<TableCollection> = {
    title: 'Acción',
    width: 120,
    dataIndex: 'actionStatus',
    render: (actionStatus: ActionStatus, row: TableCollection) => {
      if (
        state === JourneyState.Started &&
        actionStatus === ActionStatus.Pending &&
        row.arrivedAt
      ) {
        return (
          <Popconfirm
            title="¿Liberar la espera?"
            description="La parada se cierra como no colectada, igual que si el driver hubiera registrado la visita, y puede seguir con la ruta. Queda registrado que lo decidiste vos."
            okText="Liberar"
            cancelText="Cancelar"
            placement="topRight"
            disabled={isReleasing}
            onConfirm={() => {
              if (!id) return;
              releaseWait({ journeyId: id, collectionId: row.collectionId });
            }}
          >
            <Button size="small" loading={isReleasing}>
              Liberar espera
            </Button>
          </Popconfirm>
        );
      }
      if (isTransferred(row)) {
        return (
          <Flex gap={8} align="center" style={{ color: '#8c8c8c' }}>
            <SwapOutlined style={{ fontSize: 18 }} />
            <span>
              Transferida
              {row.transferredTo && (
                <>
                  {' a '}
                  <Link to={`${SITELINKS.journey}/${row.transferredTo}`}>
                    {row.transferredTo}
                  </Link>
                </>
              )}
            </span>
          </Flex>
        );
      }
      if (actionStatus === ActionStatus.Completed && !isIngested(row)) {
        return (
          <Button size="small" onClick={() => setIntakeId(row.collectionId)}>
            Ingresar
          </Button>
        );
      }
      const descriptor = ACTION_STATUS_DESCRIPTOR[actionStatus];
      if (!descriptor) return null;
      return (
        <Flex gap={8} align="center" style={issueStyle(row)}>
          {descriptor.icon}
          {descriptor.label}
        </Flex>
      );
    },
    sorter: (a, b) =>
      (a.actionStatus ?? '').localeCompare(b.actionStatus ?? ''),
  };

  const detailColumn: TableColumnProps<TableCollection> = {
    align: 'center',
    width: 40,
    render: (row: TableCollection) => (
      <Tooltip title="Ver colecta">
        <EyeOutlined
          style={{ color: '#1677ff', fontSize: 18, cursor: 'pointer' }}
          onClick={() => setDetailId(row.collectionId)}
        />
      </Tooltip>
    ),
  };

  const deleteColumn: TableColumnProps<TableCollection> = {
    align: 'center',
    width: 40,
    render: (row: TableCollection) => (
      <Popconfirm
        title="Quitar"
        description={
          row.collection?.type === 'on_demand'
            ? '¿Quitar la colecta de la ruta? Vuelve a A demanda para sumarla a otra ruta.'
            : '¿Quitar la colecta de la ruta? Se eliminará. Para volver a la tienda, agregala nuevamente.'
        }
        onConfirm={() => {
          if (!id) return;
          setToRemove(row.collectionId);
          removeCollection({
            journeyId: id,
            collectionId: row.collectionId,
            storeName: row.collection?.storeName,
          });
        }}
        okText="Quitar"
        cancelText="Cancelar"
        placement="topRight"
        icon={<DeleteOutlined style={{ color: 'red' }} />}
        disabled={isPending}
      >
        <Tooltip title="Quitar">
          {isPending && toRemove === row.collectionId ? (
            <LoadingOutlined style={{ color: 'grey', fontSize: 18 }} />
          ) : (
            <DeleteOutlined style={{ color: 'red', fontSize: 18 }} />
          )}
        </Tooltip>
      </Popconfirm>
    ),
  };

  const amountColumns = collectionAmountsEnabled ? [amountColumn] : [];

  const columns =
    state === JourneyState.Draft
      ? [
          storeColumn,
          startColumn,
          durationColumn,
          ...amountColumns,
          statusColumn,
          actionStatusColumn,
          detailColumn,
          deleteColumn,
        ]
      : [
          storeColumn,
          startColumn,
          durationColumn,
          ...amountColumns,
          statusColumn,
          actionStatusColumn,
          detailColumn,
        ];

  return (
    <>
      <CollectionDetailModal
        collectionId={detailId}
        onClose={() => setDetailId(null)}
      />
      <CollectionIntakeModal
        collectionId={intakeId}
        onClose={() => {
          setIntakeId(null);
          refetch();
        }}
      />
      <Table
        style={{ padding: '0 0 4px 8px', background: '#0DB8B2' }}
        size="small"
        rowKey={(row) => row.collectionId}
        dataSource={collections}
        columns={columns}
        pagination={false}
        scroll={{ x: true }}
      />
    </>
  );
};

export default CollectionsTable;
